import {
  Fragment,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { CaretDown, Check, Copy } from "@phosphor-icons/react";
import { cn } from "../../utils/cn";
import { IconButton } from "../IconButton";

/** One selectable language/variant of a snippet, shown as a header tab. */
export interface CodeBlockTab {
  /** Tab label (and language cue), e.g. "python", "typescript". */
  label: string;
  /** The snippet for this tab — rendered and used as the copy payload. */
  code: string;
}

export interface CodeBlockProps extends HTMLAttributes<HTMLDivElement> {
  /** Full snippet text — rendered verbatim and used as the copy payload. Omit
   *  when using `tabs`. */
  code?: string;
  /** Optional header label: a filename or language ("bash", ".env", "python").
   *  Ignored when `tabs` is set (the tabs carry the labels). */
  label?: string;
  /** Multiple selectable variants of the snippet (e.g. python / typescript),
   *  shown as header tabs. Takes precedence over `code`. */
  tabs?: CodeBlockTab[];
  /** Accessible name for the copy button. */
  copyLabel?: string;
  /** Whether to show the copy button. Disable it for illustrative examples. */
  copyable?: boolean;
  /** Whether the snippet text can be selected. */
  selectable?: boolean;
  /** Whether the snippet body may scroll vertically. */
  scrollable?: boolean;
  /** Whether to render the muted background fill. */
  filled?: boolean;
  /** Called with the copied snippet after a successful copy-to-clipboard.
   *  Optional hook for callers that want to observe copies (e.g. analytics);
   *  the copy itself works without it. Named to avoid clashing with the native
   *  `onCopy` clipboard-event handler on the root div. */
  onCopied?: (code: string) => void;
  /** Static substrings to color as personalized values (e.g. the gateway URL
   *  or a selected agent id) — marked as filled in, but not editable. */
  highlights?: string[];
  /** Clamp the snippet body to this many lines; taller snippets scroll
   *  vertically. Sized off the `--ai21-code-lh` token so contexts that
   *  restyle code type (e.g. `.onboarding`) stay in step. */
  maxLines?: number;
  /** Initially show this many lines behind a faded "Show more" control. */
  collapsedLines?: number;
  /** Controlled expanded state for a collapsible block. */
  expanded?: boolean;
  /** Called when a collapsible block is manually toggled. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Toggle text while collapsed. Pass null for a chevron-only control. */
  collapsedLabel?: string | null;
  /** Toggle text while expanded. Pass null for a chevron-only control. */
  expandedLabel?: string | null;
  /** Soft-wrap long lines instead of scrolling horizontally. A line holding
   *  literal `\n` escapes (e.g. ANTHROPIC_CUSTOM_HEADERS' joined headers) wraps
   *  at those escapes only, one part per row. Display-only: copy is unchanged. */
  wrap?: boolean;
}

/* Lightweight, dependency-free accenting — not a real tokenizer. Per line, it
 * colors three things, in priority order: personalized `highlights` (brand),
 * quoted strings (success), and trailing comments (dim). Priority matters
 * because a highlighted value (e.g. an agent id) usually sits inside a string;
 * marking it brand wins over the surrounding string color. */
type SegmentKind = "text" | "highlight" | "string" | "comment";
interface Segment {
  kind: SegmentKind;
  text: string;
}

const KIND_CLASS: Record<SegmentKind, string | null> = {
  text: null,
  highlight: "text-brand-600",
  string: "text-success-fg",
  comment: "text-fg-3",
};

/** The comment lead-in for a language label ("//" for ts/js, "#" otherwise),
 *  or null to skip comment accenting. */
function commentLead(label: string): string | null {
  const l = label.toLowerCase();
  if (
    l.includes("typescript") ||
    l.includes("javascript") ||
    l === "ts" ||
    l === "js"
  )
    return "//";

  if (l.includes("json")) return null; // JSON has no comments

  return "#"; // bash, python, .env, generic
}

/** Finds the next quoted-string span (single or double quotes) at/after `from`,
 *  or null. Naive: no escape handling — fine for these snippets. */
function nextString(
  s: string,
  from: number,
): { at: number; end: number } | null {
  let best: { at: number; end: number } | null = null;
  for (const q of ['"', "'"]) {
    const at = s.indexOf(q, from);
    if (at === -1) continue;

    const close = s.indexOf(q, at + 1);
    if (close === -1) continue;

    if (best === null || at < best.at) best = { at, end: close + 1 };
  }
  return best;
}

/* Accents the runs of a line that aren't already a personalized highlight:
 * a comment lead-in colors the rest of the line, and quoted runs become
 * strings. (Highlights are carved out first, in segmentLine, so they win.) */
function accentRun(run: string, lead: string | null): Segment[] {
  const out: Segment[] = [];
  let rest = run;
  while (rest.length > 0) {
    const commentAt = lead ? rest.indexOf(lead) : -1;
    const str = nextString(rest, 0);
    const strAt = str?.at ?? -1;

    // Comment wins a tie only if it starts no later than the next string.
    if (commentAt !== -1 && (strAt === -1 || commentAt <= strAt)) {
      if (commentAt > 0)
        out.push({ kind: "text", text: rest.slice(0, commentAt) });

      out.push({ kind: "comment", text: rest.slice(commentAt) });
      break;
    }

    if (str) {
      if (str.at > 0) out.push({ kind: "text", text: rest.slice(0, str.at) });

      // A quoted TOML/INI key ("x-upstream" = …) reads like a bare key, not a
      // value — render it plain so it matches the unquoted keys on other lines.
      const isKey = /^[ \t]*=/.test(rest.slice(str.end));
      out.push({
        kind: isKey ? "text" : "string",
        text: rest.slice(str.at, str.end),
      });
      rest = rest.slice(str.end);
    } else {
      out.push({ kind: "text", text: rest });
      break;
    }
  }
  return out;
}

/* Splits one line into accent segments. Personalized highlights are carved out
 * first (they win over strings/comments — an agent id usually sits inside a
 * string); the gaps between highlights then get string/comment accenting. */
function segmentLine(
  line: string,
  highlights: string[],
  lead: string | null,
): Segment[] {
  const out: Segment[] = [];
  let rest = line;
  while (rest.length > 0) {
    let hlAt = -1;
    let hlText = "";
    for (const h of highlights) {
      if (h.length === 0) continue;

      const at = rest.indexOf(h);
      if (at !== -1 && (hlAt === -1 || at < hlAt)) {
        hlAt = at;
        hlText = h;
      }
    }

    if (hlAt === -1) {
      out.push(...accentRun(rest, lead));
      break;
    }

    const before = rest.slice(0, hlAt);
    const after = rest.slice(hlAt + hlText.length);
    // A highlighted value sitting inside quotes (base_url = "…/gateway/v1"):
    // the opening quote, any literal tail before the closing quote, and the
    // closing quote would otherwise orphan into adjacent runs (no pair to
    // match) and render as plain text — color them as string so they match the
    // quoted value. The trailing quote of `before` opens a string only when
    // that quote char appears an odd number of times in `before`.
    const q = before.slice(-1);
    const opensString =
      (q === '"' || q === "'") && (before.split(q).length - 1) % 2 === 1;
    const closeAt = opensString ? after.indexOf(q) : -1;

    if (closeAt !== -1) {
      if (before.length > 1) out.push(...accentRun(before.slice(0, -1), lead));

      out.push({ kind: "string", text: q });
      out.push({ kind: "highlight", text: hlText });
      out.push({ kind: "string", text: after.slice(0, closeAt + 1) });
      rest = after.slice(closeAt + 1);
    } else {
      if (before.length > 0) out.push(...accentRun(before, lead));

      out.push({ kind: "highlight", text: hlText });
      rest = after;
    }
  }
  return out;
}

/* Splits a segment at literal `\n` escapes into chunks that can't wrap
 * internally (`whitespace-pre`), separated by a break opportunity. On a line
 * whose value is newline-joined — ANTHROPIC_CUSTOM_HEADERS packs three headers
 * into one string — that makes the `\n`s the only place the line can break, so
 * each header lands on its own visual row instead of the browser breaking at
 * the last hyphen that happens to fit. Chunks hold the original characters, so
 * the rendered text (and any manual selection) still matches the copy payload. */
function chunkAtEscapes(text: string): ReactNode {
  const parts = text.split("\\n");

  return parts.map((part, i) => {
    const last = i === parts.length - 1;
    return (
      <Fragment key={i}>
        <span className="whitespace-pre">
          {part}
          {!last && "\\n"}
        </span>
        {!last && <wbr />}
      </Fragment>
    );
  });
}

/** Height of `lines` body lines plus the pre's own p-3 (0.75rem × 2). Sized off
 *  the `--ai21-code-lh` token so contexts that restyle code type stay in step. */
function linesHeight(lines: number): string {
  if (lines === 0) return "0px";

  return `calc(${lines} * var(--ai21-code-lh, 20px) + 1.5rem)`;
}

/** Renders `code` with lightweight accenting (strings, comments, highlights).
 *  Scanned per line so a comment lead-in only affects its own line. */
function renderBody(
  code: string,
  highlights: string[],
  label: string,
  wrap: boolean,
): ReactNode {
  const lead = commentLead(label);
  const lines = code.split("\n");

  return lines.map((line, li) => {
    const segs = segmentLine(line, highlights, lead);
    // Only lines carrying `\n` escapes get chunked; the rest soft-wrap normally
    // (a long URL should still break, and has no header boundary to break at).
    const chunked = wrap && line.includes("\\n");
    return (
      <Fragment key={li}>
        {li > 0 && "\n"}
        {segs.map((seg, i) => {
          const cls = KIND_CLASS[seg.kind];
          const body = chunked ? chunkAtEscapes(seg.text) : seg.text;
          return cls ? (
            <span key={i} className={cls}>
              {body}
            </span>
          ) : (
            <Fragment key={i}>{body}</Fragment>
          );
        })}
      </Fragment>
    );
  });
}

/** Monospace snippet with copy-to-clipboard and lightweight accenting (quoted
 *  strings, trailing comments — keyed off the `label`/tab language, not a real
 *  tokenizer). Feedback is a transient ✓ on the trigger (the app has no toast
 *  system). `highlights` color personalized substrings (a selected agent id,
 *  the gateway URL) so the reader sees what's filled in. Pass `tabs` for
 *  switchable language variants. */
export function CodeBlock({
  code,
  label,
  tabs,
  copyLabel = "Copy code",
  copyable = true,
  selectable = true,
  scrollable = true,
  filled = true,
  highlights,
  maxLines,
  collapsedLines,
  expanded: controlledExpanded,
  onExpandedChange,
  collapsedLabel = "Show more",
  expandedLabel = "Show less",
  wrap = false,
  onCopied,
  className,
  ...rest
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const [active, setActive] = useState(0);
  const [internalExpanded, setInternalExpanded] = useState(false);
  // Measured body height, kept while expanded so max-height has a concrete
  // endpoint to transition between in both directions.
  const [openHeight, setOpenHeight] = useState<number>();
  const bodyRef = useRef<HTMLPreElement>(null);
  const highlightList = highlights ?? [];

  // Normalize to a tab list: explicit tabs win; otherwise one tab from `code`
  // (labelled if `label` was given). `active` is clamped in case tabs shrink.
  const tabList: CodeBlockTab[] =
    tabs && tabs.length > 0 ? tabs : [{ label: label ?? "", code: code ?? "" }];
  const current = tabList[Math.min(active, tabList.length - 1)];
  const showTabs = (tabs?.length ?? 0) > 1;
  const headerLabel = showTabs ? null : current.label || null;
  const collapsible = collapsedLines !== undefined;
  const expanded = controlledExpanded ?? internalExpanded;
  const toggleLabel = expanded ? expandedLabel : collapsedLabel;
  const clampLines = collapsible && !expanded ? collapsedLines : maxLines;
  // Expanded, the clamp is the measured height rather than a line count: it
  // keeps max-height animatable and stays right when the snippet soft-wraps.
  const bodyMaxHeight =
    collapsible && expanded
      ? openHeight && `${openHeight}px`
      : clampLines !== undefined && linesHeight(clampLines);

  const toggleExpanded = () => {
    // Measured before the flip so opening and closing share an endpoint. A 0
    // reading means unmeasurable (jsdom, or a hidden block) — skip the clamp
    // rather than animating the body down to nothing.
    if (!expanded) setOpenHeight(bodyRef.current?.scrollHeight);

    const nextExpanded = !expanded;
    if (controlledExpanded === undefined) setInternalExpanded(nextExpanded);

    onExpandedChange?.(nextExpanded);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(current.code);
      setCopied(true);
      onCopied?.(current.code);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (permissions/insecure context) — the code
      // stays selectable as text.
    }
  };

  const copyButton = copyable ? (
    <IconButton
      variant="ghost"
      size="sm"
      icon={copied ? <Check className="text-success-fg" /> : <Copy />}
      aria-label={copyLabel}
      onClick={copy}
    />
  ) : null;

  const hasHeader = showTabs || headerLabel !== null;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border",
        filled && "bg-surface-muted",
        className,
      )}
      {...rest}
    >
      {showTabs ? (
        <div
          role="tablist"
          aria-label="Snippet language"
          className="flex items-center gap-1 border-b pl-1.5 pr-1.5"
        >
          {tabList.map((t, i) => (
            <button
              key={t.label}
              type="button"
              role="tab"
              aria-selected={i === active}
              onClick={() => setActive(i)}
              className={cn(
                "border-b-2 px-2.5 py-1.5 font-mono text-body-sm transition-colors",
                i === active
                  ? "border-brand-600 text-fg-1"
                  : "border-transparent text-fg-3 hover:text-fg-2",
              )}
            >
              {t.label}
            </button>
          ))}
          <div className="flex-1" />
          {copyButton}
        </div>
      ) : headerLabel !== null ? (
        <div className="flex items-center justify-between border-b py-1.5 pl-3 pr-1.5">
          <span className="font-mono text-body-sm text-fg-3">
            {headerLabel}
          </span>
          {copyButton}
        </div>
      ) : (
        <div className="absolute right-1.5 top-1.5">{copyButton}</div>
      )}
      <div className="relative">
        <pre
          ref={bodyRef}
          className={cn(
            // overflow-x-auto stays on when wrapping: a chunk that can't break
            // (a single very long header) then scrolls instead of bleeding out.
            "overflow-x-auto",
            collapsedLines === 0 && !expanded ? "p-0" : "p-3",
            wrap && "whitespace-pre-wrap [overflow-wrap:anywhere]",
            !hasHeader && copyable && "pr-12",
            !selectable && "select-none",
            collapsible &&
              "transition-[max-height] duration-300 ease-out motion-reduce:transition-none",
            !scrollable
              ? "overflow-y-hidden"
              : collapsible && !expanded
                ? "overflow-y-hidden"
                : (collapsible || clampLines !== undefined) &&
                  "overflow-y-auto",
          )}
          style={bodyMaxHeight ? { maxHeight: bodyMaxHeight } : undefined}
        >
          <code className="font-mono text-code text-fg-1">
            {renderBody(current.code, highlightList, current.label, wrap)}
          </code>
        </pre>
        {collapsible && filled && (
          // Fades the clamped tail so the cut reads as "more below" rather than
          // a snippet that happens to end mid-config.
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-surface-muted transition-opacity duration-300 motion-reduce:transition-none",
              expanded && "opacity-0",
            )}
          />
        )}
      </div>
      {/* Kept in normal flow in both states — an overlay that becomes a row on
       *  expand moves the control out from under the cursor mid-animation. */}
      {collapsible && (
        <button
          type="button"
          className={cn(
            "flex w-full items-center justify-center gap-1 py-1.5 text-body-sm text-fg-3 transition-colors hover:text-fg-2",
            (expanded || hasHeader || collapsedLines !== 0) && "border-t",
            filled && "hover:bg-surface",
          )}
          aria-expanded={expanded}
          aria-label={
            toggleLabel ??
            `${expanded ? "Collapse" : "Expand"} ${headerLabel ?? "code"}`
          }
          onClick={toggleExpanded}
        >
          {toggleLabel}
          <CaretDown
            aria-hidden
            className={cn(
              "size-3.5 transition-transform duration-300 ease-out motion-reduce:transition-none",
              expanded && "-rotate-180",
            )}
          />
        </button>
      )}
    </div>
  );
}
